/**
 *
 * QuestionRating
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import _has from 'lodash/has';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(theme => ({
  ratingWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
  },
  ratingWrapperAlt: {
    flexDirection: 'column',
  },
  ratingText: {
    fontSize: 16,
  },
  ratingTextAlt: {
    maxWidth: '100px',
    '&:last-child': {
      textAlign: 'right',
    },
  },
  verticalRatingText: {
    textAlign: 'center !important',
    maxWidth: '100%',
  },
  ratingsContainer: {
    display: 'flex',
    marginLeft: 8,
    marginRight: 8,
  },
  ratingsContainerAlt: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: 8,
    marginBottom: 8,
  },
  verticalRatingsContainer: {
    display: 'block',
  },
  ratingContext: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  ratingContextAlt: {
    flexDirection: 'column',
  },
  rating: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    outline: 0,
    width: 42,
    padding: '4px 0',
    '&:hover': {
      opacity: 0.8,
    },
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    marginLeft: 2,
    marginRight: 2,
    borderRadius: 2,
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  ratingAlt: {
    padding: '4px 0',
  },
  verticalRating: {
    margin: '4px 0',
  },
  selectedRating: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
  labelsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  verticalLabelsContainer: {
    justifyContent: 'center',
  },
}));

function QuestionRating({
  name,
  minRateDescription,
  maxRateDescription,
  rateValues,
  rateMin = 1,
  rateMax = 5,
  answers,
  onChange,
}) {
  const classes = useStyles();
  const isMobileOrLess = useMediaQuery('(max-width:600px)');
  const isTabletOrLess = useMediaQuery('(max-width:768px)');
  const lowDesktopMin = useMediaQuery('(min-width:960px)');
  const lowDesktopMax = useMediaQuery('(max-width:1024px)');
  const isLowDesktop = lowDesktopMin && lowDesktopMax;

  let formattedRateValues =
    rateValues ||
    Array.from({ length: rateMax - rateMin + 1 }, (_, k) =>
      (k + rateMin).toString(),
    );
  if (
    !_has(formattedRateValues, '0.value') ||
    !_has(formattedRateValues, '0.text')
  ) {
    formattedRateValues = formattedRateValues.map(el => ({
      text: el,
      value: el,
    }));
  }

  const shouldBeVertical =
    (isMobileOrLess && formattedRateValues.length > 7) ||
    (isTabletOrLess && formattedRateValues.length > 9);
  const shouldDisplayLabelOnLine =
    isTabletOrLess || (isLowDesktop && formattedRateValues.length > 7);

  return (
    <div
      className={classNames({
        [classes.ratingWrapper]: true,
        [classes.ratingWrapperAlt]: shouldDisplayLabelOnLine,
      })}
    >
      <div
        className={classNames({
          [classes.ratingContext]: true,
          [classes.ratingContextAlt]: shouldDisplayLabelOnLine,
        })}
      >
        {!shouldDisplayLabelOnLine && (
          <Typography
            className={classNames({
              [classes.ratingText]: true,
              [classes.ratingTextAlt]: shouldDisplayLabelOnLine,
            })}
            onClick={() => onChange(name, formattedRateValues[0].value)}
          >
            {minRateDescription}
          </Typography>
        )}
        {shouldBeVertical && (
          <div
            className={classNames({
              [classes.labelsContainer]: true,
              [classes.verticalLabelsContainer]: true,
            })}
          >
            <Typography
              className={classNames({
                [classes.ratingText]: true,
                [classes.ratingTextAlt]: shouldDisplayLabelOnLine,
                [classes.verticalRatingText]: true,
              })}
              onClick={() => onChange(name, formattedRateValues[0].value)}
            >
              {minRateDescription}
            </Typography>
          </div>
        )}
        <div
          className={classNames({
            [classes.ratingsContainer]: true,
            [classes.ratingsContainerAlt]: shouldDisplayLabelOnLine,
            [classes.verticalRatingsContainer]: shouldBeVertical,
          })}
        >
          {formattedRateValues.map(el => (
            <div
              key={el.value}
              onClick={() => onChange(name, el.value)}
              onKeyPress={() => onChange(name, el.value)}
              role="button"
              tabIndex={0}
              className={classNames({
                [classes.rating]: true,
                [classes.ratingAlt]: shouldDisplayLabelOnLine,
                [classes.selectedRating]: answers && answers === el.value,
                [classes.verticalRating]: shouldBeVertical,
              })}
            >
              {el.text}
            </div>
          ))}
        </div>
        {!shouldDisplayLabelOnLine && (
          <Typography
            className={classNames({
              [classes.ratingText]: true,
              [classes.ratingTextAlt]: shouldDisplayLabelOnLine,
            })}
            onClick={() =>
              onChange(
                name,
                formattedRateValues[formattedRateValues.length - 1].value,
              )
            }
          >
            {maxRateDescription}
          </Typography>
        )}
        {shouldDisplayLabelOnLine && (
          <div
            className={classNames({
              [classes.labelsContainer]: true,
              [classes.verticalLabelsContainer]: shouldBeVertical,
            })}
          >
            {!shouldBeVertical && (
              <Typography
                className={classNames({
                  [classes.ratingText]: true,
                  [classes.ratingTextAlt]: shouldDisplayLabelOnLine,
                })}
                onClick={() => onChange(name, formattedRateValues[0].value)}
              >
                {minRateDescription}
              </Typography>
            )}
            <Typography
              className={classNames({
                [classes.ratingText]: true,
                [classes.ratingTextAlt]: shouldDisplayLabelOnLine,
                [classes.verticalRatingText]: shouldBeVertical,
              })}
              onClick={() =>
                onChange(
                  name,
                  formattedRateValues[formattedRateValues.length - 1].value,
                )
              }
            >
              {maxRateDescription}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
}

QuestionRating.propTypes = {
  name: PropTypes.string,
  minRateDescription: PropTypes.string,
  maxRateDescription: PropTypes.string,
  rateValues: PropTypes.array,
  rateMin: PropTypes.number,
  rateMax: PropTypes.number,
  answers: PropTypes.any,
  onChange: PropTypes.func,
};

export default QuestionRating;
